import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { lookupsAPI } from '../utils/api';
import { toast } from 'sonner';
import SystemRoles from './SystemRoles';
import {
  List, Loader2, Plus, Pencil, Trash2, Check, X, Lock, Database, RefreshCw, ChevronRight, Search,
} from 'lucide-react';

export default function LookupMaintenance() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [types, setTypes] = useState([]);
  const [values, setValues] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingValues, setLoadingValues] = useState(false);
  const [search, setSearch] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [seeding, setSeeding] = useState(false);

  const current = types.find((t) => t.key === type);

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true);
    try { const r = await lookupsAPI.getTypes(); setTypes(r.data || []); }
    catch { toast.error('Failed to load lookup types'); }
    finally { setLoadingTypes(false); }
  }, []);

  const loadValues = useCallback(async () => {
    if (!type) { setValues([]); return; }
    setLoadingValues(true);
    try { const r = await lookupsAPI.getValues(type); setValues(r.data || []); }
    catch (e) {
      if (e?.response?.status === 404) { toast.error('Unknown lookup type'); navigate('/lookup'); }
      else toast.error('Failed to load values');
    } finally { setLoadingValues(false); }
  }, [type, navigate]);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadValues(); }, [loadValues]);

  const addValue = async () => {
    if (!newLabel.trim()) return;
    try {
      await lookupsAPI.create(type, { label: newLabel.trim() });
      setNewLabel(''); setAdding(false);
      await loadValues(); await loadTypes();
      toast.success('Value added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add value'); }
  };

  const saveEdit = async (id) => {
    try {
      await lookupsAPI.update(type, id, { label: editLabel.trim() });
      setEditingId(null); await loadValues();
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };

  const toggleActive = async (v) => {
    try { await lookupsAPI.update(type, v.id, { active: !v.active }); await loadValues(); }
    catch { toast.error('Could not update'); }
  };

  const del = async (v) => {
    if (!window.confirm(`Delete "${v.label}"?`)) return;
    try {
      await lookupsAPI.remove(type, v.id);
      await loadValues(); await loadTypes();
      toast.success('Deleted');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'object' ? d.message : d;
      toast.error(msg || 'Could not delete');
    }
  };

  const runSeed = async () => {
    setSeeding(true);
    try { const r = await lookupsAPI.seed(); await loadTypes(); await loadValues(); toast.success('Lookup defaults loaded'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
    finally { setSeeding(false); }
  };

  const grouped = types.reduce((acc, t) => { (acc[t.group] = acc[t.group] || []).push(t); return acc; }, {});
  const filteredValues = values.filter((v) => v.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><List className="w-6 h-6 text-teal-600" /> Lookup Maintenance</h1>
          <p className="text-sm text-slate-500">Maintain all system lookup values. A value in use cannot be deleted.</p>
        </div>
        {isAdmin && (
          <button onClick={runSeed} disabled={seeding} data-testid="seed-btn"
            className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-100 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Load defaults
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-4">
        {/* Types list */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 h-fit">
          {loadingTypes ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="mb-3">
                <div className="text-xs font-semibold uppercase text-slate-400 px-2 mb-1">{group}</div>
                {items.map((t) => (
                  <button key={t.key} onClick={() => navigate(`/lookup/${t.key}`)} data-testid={`lktype-${t.key}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${type === t.key ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                    <span>{t.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${type === t.key ? 'bg-teal-500' : 'bg-slate-100 text-slate-500'}`}>{t.value_count}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Values panel */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {!type ? (
            <div className="text-center py-20 text-slate-400">
              <Database className="w-12 h-12 mx-auto mb-3 opacity-40" />
              Select a lookup type on the left to maintain its values.
            </div>
          ) : type === 'system-roles' ? (
            <SystemRoles />
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 gap-2 flex-wrap">
                <h2 className="font-semibold text-slate-800">{current?.label || type}</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
                      className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg w-40 focus:ring-2 focus:ring-teal-500 outline-none" />
                  </div>
                  {isAdmin && !adding && (
                    <button onClick={() => setAdding(true)} data-testid="lk-add-btn"
                      className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                      <Plus className="w-4 h-4" /> Add value
                    </button>
                  )}
                </div>
              </div>

              {loadingValues ? (
                <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-teal-600" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 bg-slate-50">
                        <th className="px-4 py-2 font-medium">Value</th>
                        <th className="px-4 py-2 font-medium">Usage</th>
                        <th className="px-4 py-2 font-medium">Active</th>
                        {isAdmin && <th className="px-4 py-2 w-24"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {adding && (
                        <tr className="border-t-2 border-teal-200 bg-teal-50/40">
                          <td className="px-4 py-2" colSpan={isAdmin ? 4 : 3}>
                            <div className="flex items-center gap-2">
                              <input autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && addValue()} placeholder="New value label"
                                data-testid="lk-new-input"
                                className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg" />
                              <button onClick={addValue} data-testid="lk-new-save" className="p-1.5 text-green-600 hover:bg-green-100 rounded"><Check className="w-4 h-4" /></button>
                              <button onClick={() => { setAdding(false); setNewLabel(''); }} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {filteredValues.length === 0 && !adding ? (
                        <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No values. {isAdmin && 'Click "Add value" or "Load defaults".'}</td></tr>
                      ) : filteredValues.map((v) => (
                        <tr key={v.id} className="border-t border-slate-100">
                          <td className="px-4 py-2">
                            {editingId === v.id ? (
                              <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && saveEdit(v.id)}
                                className="px-2 py-1 border border-slate-300 rounded-md w-full max-w-xs" />
                            ) : (
                              <span className="text-slate-800 font-medium">{v.label}</span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {v.usage_count > 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" title={(v.used_in || []).map((u) => `${u.collection}.${u.field}: ${u.count}`).join(', ')}>
                                <Lock className="w-3 h-3" /> {v.usage_count} in use
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Not used</span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <button disabled={!isAdmin} onClick={() => toggleActive(v)}
                              className={`px-2 py-0.5 text-xs rounded-full border ${v.active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                              {v.active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2">
                              {editingId === v.id ? (
                                <div className="flex gap-1">
                                  <button onClick={() => saveEdit(v.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                                  <button onClick={() => setEditingId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <button onClick={() => { setEditingId(v.id); setEditLabel(v.label); }} data-testid={`lk-edit-${v.id}`} className="p-1 text-slate-500 hover:bg-slate-100 rounded"><Pencil className="w-4 h-4" /></button>
                                  <button onClick={() => del(v)} data-testid={`lk-del-${v.id}`}
                                    className={`p-1 rounded ${v.usage_count > 0 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`}
                                    title={v.usage_count > 0 ? 'In use — cannot delete' : 'Delete'}>
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
